import { lazy, Suspense, type ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useFeatures } from '@/lib/features'
import { Logo } from '@/components/Logo'
import type { FeatureKey } from '@shared/features'
import { useAuth } from '@/lib/auth'
import { hasAtLeastRole, type Role } from '@shared/model'
import { AppLayout } from '@/layouts/AppLayout'
import { FullPageSpinner } from '@/components/FullPageSpinner'
import { NoAccess } from '@/pages/NoAccess'

import { SignInPage } from '@/pages/auth/SignInPage'
import { SignUpPage } from '@/pages/auth/SignUpPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ActivatePage } from '@/pages/ActivatePage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { BookletPage } from '@/pages/booklet/BookletPage'
import { CompaniesPage } from '@/pages/booklet/CompaniesPage'
import { CompanyPage } from '@/pages/booklet/CompanyPage'
import { MyCodesPage } from '@/pages/MyCodesPage'
import { AccountPage } from '@/pages/AccountPage'
import { HelpPage } from '@/pages/HelpPage'
import { TeamPage } from '@/pages/team/TeamPage'

// Adminytan är stor och används av en handfull personer på desktop.
// Den laddas separat så att köparens första sidladdning förblir liten.
const MyBookletsPage = lazy(() =>
  import('@/pages/booklet/MyBookletsPage').then((m) => ({ default: m.MyBookletsPage })),
)

const OrderPage = lazy(() =>
  import('@/pages/order/OrderPage').then((m) => ({ default: m.OrderPage })),
)
const OrderStatusPage = lazy(() =>
  import('@/pages/order/OrderStatusPage').then((m) => ({ default: m.OrderStatusPage })),
)

const AdminRoutes = lazy(() =>
  import('@/pages/admin/AdminRoutes').then((m) => ({ default: m.AdminRoutes })),
)

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageSpinner />
  if (!user) return <Navigate to="/logga-in" state={{ from: location }} replace />
  return <>{children}</>
}

function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const { role: current, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  if (!hasAtLeastRole(current, role)) return <NoAccess required={role} />
  return <>{children}</>
}

/** Redan inloggad? Då är inloggningssidan inte intressant. */
function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  if (user) return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * Visar innehållet bara när funktionen är påslagen.
 *
 * En avstängd funktion ska bete sig som att den inte finns, även för den som
 * har den gamla adressen kvar. Servern nekar också – det här är för att
 * besökaren ska få ett begripligt besked i stället för ett felmeddelande.
 */
/**
 * Spärrar en sida bakom en strömbrytare.
 *
 * Texten går att byta, för de avstängda sidorna är avstängda av olika skäl
 * och den som möter rutan ska få veta vad hen ska göra i stället. "Sidan är
 * inte öppen än" utan fortsättning får någon att maila kansliet i onödan.
 */
function RequireFeature({
  name,
  children,
  title = 'Sidan är inte öppen än',
  body = 'Häftena säljs som tryckta lappar den här perioden. Vill du köpa ett, fråga en spelare i föreningen eller hör av dig till kansliet.',
  link = { to: '/aktivera', text: 'Har du en kod? Aktivera häftet' },
}: {
  name: FeatureKey
  children: ReactNode
  title?: string
  body?: string
  link?: { to: string; text: string }
}) {
  const { features, loading } = useFeatures()

  // Inget medan svaret är på väg. Att blinka förbi sidan och sedan ta bort
  // den är värre än att vänta en halv sekund.
  if (loading) return null
  if (features[name]) return <>{children}</>

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-4 px-4 py-8 text-center">
      <Logo size={52} tone="ink" />
      <h1 className="font-display text-xl font-extrabold">{title}</h1>
      <p className="text-sm text-muted">{body}</p>
      <Link to={link.to} className="text-sm font-semibold text-brand-ink underline">
        {link.text}
      </Link>
    </main>
  )
}

export function App() {
  return (
    <Routes>
      {/* Öppna sidor */}
      <Route
        path="/logga-in"
        element={
          <RedirectIfSignedIn>
            <SignInPage />
          </RedirectIfSignedIn>
        }
      />
      {/* Kontoskapandet är stängt fram till lanseringen. Hela rutten och inte
          bara knappen: adressen hamnar i lagchattar så fort någon provar. */}
      <Route
        path="/skapa-konto"
        element={
          <RequireFeature
            name="signUp"
            title="Appen öppnar snart"
            body="Lirahäftet är inte igång än. När häftena delats ut skapar du ett konto här och aktiverar koden på din lapp."
            link={{ to: '/logga-in', text: 'Har du redan ett konto? Logga in' }}
          >
            <RedirectIfSignedIn>
              <SignUpPage />
            </RedirectIfSignedIn>
          </RequireFeature>
        }
      />
      <Route
        path="/glomt-losenord"
        element={
          <RedirectIfSignedIn>
            <ForgotPasswordPage />
          </RedirectIfSignedIn>
        }
      />

      {/* Aktivering fungerar både utloggad (QR från lappen) och inloggad. */}
      <Route path="/aktivera" element={<ActivatePage />} />

      {/* Integritetspolicyn är öppen med flit. Den som funderar på att skapa
          konto ska kunna läsa vad det innebär innan hen gör det – en policy
          bakom en inloggning är ingen policy. */}
      <Route path="/integritet" element={<PrivacyPage />} />

      {/* Beställning kräver inget konto – att kräva inloggning vore att lägga
          tillbaka precis den tröskel flödet finns för att ta bort.

          Ligger bakom en strömbrytare: första upplagan säljs på papper, och
          adressen finns kvar i gamla lagchattar. Att bara dölja knappen hade
          lämnat sidan öppen för den som har länken. */}
      <Route
        path="/bestall"
        element={
          <RequireFeature name="digitalOrders">
            <OrderPage />
          </RequireFeature>
        }
      />
      {/* Lagets egen länk. Lagledaren sprider den i kanalen föräldrarna redan
          använder, och laget är förvalt när de kommer fram. */}
      <Route
        path="/bestall/lag/:teamId"
        element={
          <RequireFeature name="digitalOrders">
            <OrderPage />
          </RequireFeature>
        }
      />
      <Route
        path="/bestall/:reference"
        element={
          <RequireFeature name="digitalOrders">
            <OrderStatusPage />
          </RequireFeature>
        }
      />

      {/* Inloggade sidor */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<BookletPage />} />
        <Route path="foretag" element={<CompaniesPage />} />
        <Route path="foretag/:companyId" element={<CompanyPage />} />
        <Route path="mina-koder" element={<MyCodesPage />} />
        <Route path="mina-haften" element={<MyBookletsPage />} />
        <Route path="konto" element={<AccountPage />} />
        <Route path="hjalp" element={<HelpPage />} />

        <Route
          path="lag"
          element={
            <RequireRole role="teamLeader">
              <TeamPage />
            </RequireRole>
          }
        />
      </Route>

      {/* Adminytan har egen layout och laddas separat. */}
      <Route
        path="/admin/*"
        element={
          <RequireAuth>
            <RequireRole role="admin">
              <Suspense fallback={<FullPageSpinner />}>
                <AdminRoutes />
              </Suspense>
            </RequireRole>
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
